import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { getProjectContent } from '../../../../features/sessions/sessionProjectManagementPlayer/sliceProjectManagementPlayer';
import {
    Box, Chip, Dialog, DialogContent, DialogTitle,
    IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { UserAvatar } from '../../../userAvatar/UserAvatar';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface ProjectContentInfoProps {
    projectContentId: string;
    userNicknameUrlParams: string;
    onCloseClick: (confirmation: boolean) => void;
}

export function ProjectContentInfo({ projectContentId, userNicknameUrlParams, onCloseClick }: ProjectContentInfoProps) {
    const authHeaders           = useSelector((state: RootState) => state.session.authHeaders);
    const dispatch              = useDispatch<AppDispatch>();
    const currentProjectContent = useSelector((state: RootState) => state.sessionProjectPlayerManagement.currentProjectContentToShow);

    useEffect(() => {
        dispatch(getProjectContent({
            authHeaders,
            routeParams: {
                projectContentId: `${projectContentId}`,
                userNickname: userNicknameUrlParams,
            },
        }));
    }, []);

    function handleFormatDate(dateString: string | undefined): string | null {
        if (!dateString) return null;
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        };
        return date.toLocaleString(navigator.language, options);
    }

    function centisecondsToTime(centiseconds: number | string): string {
        const cs = typeof centiseconds === 'string' ? parseInt(centiseconds) : centiseconds;
        const totalSeconds = Math.floor(cs / 100);
        const minutes      = Math.floor(totalSeconds / 60);
        const seconds      = totalSeconds % 60;
        const rem          = cs % 100;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
    }

    const content = currentProjectContent?.content;

    return (
        <Dialog
            open
            onClose={() => onCloseClick(false)}
            fullWidth
            maxWidth='sm'
            PaperProps={{
                sx: {
                    bgcolor: BG_SECTION,
                    border: BORDER_SUBTLE,
                    borderRadius: '12px',
                    color: TEXT_PRIMARY,
                },
            }}
        >
            {/* ── Título ── */}
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                pb: 1, borderBottom: BORDER_SUBTLE,
            }}>
                <Box>
                    <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>
                        {content?.title}
                    </Typography>
                    <Box sx={{ width: 32, height: 3, bgcolor: BRAND_RED, mt: 0.5 }} />
                </Box>
                <IconButton
                    onClick={() => onCloseClick(false)}
                    size='small'
                    sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}
                >
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Descrição */}
                {content?.description && (
                    <Typography variant='body2' color={TEXT_SECONDARY}>
                        {content.description}
                    </Typography>
                )}

                {/* Chips: download · duração · instrumento */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        icon={content?.downloadAbled
                            ? <LockOpenIcon sx={{ fontSize: '14px !important', color: '#4caf50 !important' }} />
                            : <LockIcon    sx={{ fontSize: '14px !important', color: '#e57373 !important' }} />
                        }
                        label={content?.downloadAbled ? 'Download enabled' : 'Download disabled'}
                        size='small'
                        sx={{
                            bgcolor: content?.downloadAbled ? 'rgba(76,175,80,0.12)' : 'rgba(231,115,115,0.12)',
                            color:   content?.downloadAbled ? '#4caf50' : '#e57373',
                            fontWeight: 500,
                        }}
                    />
                    {content?.file?.blob.metadata.centiseconds && (
                        <Chip
                            label={centisecondsToTime(content.file.blob.metadata.centiseconds)}
                            size='small'
                            sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: TEXT_PRIMARY }}
                        />
                    )}
                    {content?.instrument?.name && (
                        <Chip
                            label={content.instrument.name}
                            size='small'
                            sx={{ bgcolor: BRAND_RED, color: '#fff', fontWeight: 600 }}
                        />
                    )}
                </Box>

                {/* Info do autor */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.5, bgcolor: BG_CARD, borderRadius: '8px', border: BORDER_SUBTLE,
                }}>
                    <UserAvatar
                        src={content?.user?.userMoreInfo?.avatarUrl}
                        size={48}
                        sx={{ cursor: 'pointer' }}
                    />
                    <Typography
                        variant='body2'
                        fontWeight={600}
                        color={TEXT_PRIMARY}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                        {content?.user?.nickname}
                    </Typography>
                </Box>

                {/* Data de attach */}
                {currentProjectContent?.createdAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant='caption' color={TEXT_SECONDARY}>Attached at:</Typography>
                        <Typography variant='caption' color={TEXT_PRIMARY}>
                            {handleFormatDate(`${currentProjectContent.createdAt}`)}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
